import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  RowData,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

export type { ColumnDef } from '@tanstack/react-table';
import React from 'react';
import { cn } from '../../utils';
import { Alert, AlertDescription } from '../../components/core/alert';
import { Button } from '../../components/core/button';
import { Input } from '../../components/core/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/core/select';
import { ArrowDown, ArrowUp, ArrowUpDown, SearchXIcon } from 'lucide-react';

declare module '@tanstack/react-table' {
  //allows us to define custom properties for our columns
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'range' | 'select'
  }
}

type TableProps<T> = {
  tableData: T[] | null;
  columns: ColumnDef<T, any>[];
  getRowId: (row: T) => any;
  globalFilter?: string;
  onRowClick?: ((row: T) => void) | null;
};

export const Table = <T,>({ tableData, columns, getRowId, globalFilter = "", onRowClick = null }: TableProps<T>) => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilterValue, setGlobalFilterValue] = React.useState(globalFilter);
  const [data, setData] = React.useState<T[]>(tableData ?? []);
  const [sorting, setSorting] = React.useState<SortingState>([]);

  React.useEffect(() => {
    if (tableData) {
      setData(tableData);
    }
  }, [tableData]);

  React.useEffect(() => {
    setGlobalFilterValue(globalFilter);
  }, [globalFilter]);

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter: globalFilterValue,
      sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilterValue,
    onSortingChange: setSorting,
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
    getRowId: originalRow => getRowId(originalRow),
  });

  return (
    <div className="w-full">
      <div className="rounded-md border">
        <table className="w-full caption-bottom text-sm">
          <thead className="[&_tr]:border-b">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
              {headerGroup.headers.map(header => {
                // @ts-expect-error - columnDef.meta type is not properly recognized but it's handled safely
                if (header.column.columnDef.meta?.isHidden) return null;
                const canSort = header.column.getCanSort();
                const sortingState = header.column.getIsSorted();

                const SortingIndicator = () => {
                  if (!sortingState) {
                    return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
                  }

                  if (sortingState === 'asc') {
                    return <ArrowUp className="h-4 w-4 text-muted-foreground" />;
                  }

                  return <ArrowDown className="h-4 w-4 text-muted-foreground" />;
                };

                return (
                  <th key={header.id} className="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0">
                    {canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-2"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <SortingIndicator />
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
          </thead>
          <tbody className="[&_tr:last-child]:border-0">
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-8">
                <Alert variant="info" className="mx-auto max-w-md border-none shadow-none flex items-center justify-center flex-col text-center py-6">
                  <SearchXIcon className="h-10 w-10 mb-3 text-muted-foreground/70" />
                  <AlertDescription className="text-muted-foreground">
                    No data available
                  </AlertDescription>
                </Alert>
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <tr key={row.id}
                  className={"border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted" + (onRowClick ? " cursor-pointer" : "")}
                  onClick={() => { if(onRowClick) onRowClick(row.original); }}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className="p-4 align-middle [&:has([role=checkbox])]:pr-0">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between space-x-6 py-4">
        <div className="text-sm text-muted-foreground">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {'<<'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {'<'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {'>'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {'>>'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// This is the fuzzy filter function
function fuzzyFilter<T>(
  row: any, 
  columnId: string, 
  value: string, 
  addMeta: (meta: any) => void
): boolean {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
}

// This is needed for the fuzzy filter function
function rankItem(row: any, value: string): { passed: boolean } {
  return {
    // Very simple ranking - just check if the value is in the row
    passed: String(row).toLowerCase().includes(String(value).toLowerCase())
  };
}

// Radix Select items cannot use an empty-string value, so "All" uses a sentinel
// that maps back to an empty filter.
const ALL_FILTER_VALUE = '__all__';

function Filter({ column }: { column: Column<any, unknown> }) {
  const columnFilterValue = column.getFilterValue()
  const { filterVariant } = column.columnDef.meta ?? {}

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        {/* See faceted column filters example for min max values functionality */}
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max`}
          className="w-24 border shadow rounded"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <Select
      onValueChange={value => column.setFilterValue(value === ALL_FILTER_VALUE ? '' : value)}
      value={columnFilterValue?.toString() || ALL_FILTER_VALUE}
    >
      <SelectTrigger className="h-9 w-36">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {/* See faceted column filters example for dynamic select options */}
        <SelectItem value={ALL_FILTER_VALUE}>All</SelectItem>
        <SelectItem value="complicated">complicated</SelectItem>
        <SelectItem value="relationship">relationship</SelectItem>
        <SelectItem value="single">single</SelectItem>
      </SelectContent>
    </Select>
  ) : (
    <DebouncedInput
      className="w-36 border shadow rounded"
      onChange={value => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
    // See faceted column filters example for datalist search suggestions
  )
}

// A typical debounced input react component
function DebouncedInput({
                          value: initialValue,
                          onChange,
                          debounce = 500,
                          ...props
                        }: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <Input {...props} value={value} onChange={e => setValue(e.target.value)} />
  )
}
